import { useMemo } from 'react';

import { PrintProps } from '../multiple-choice.constants';
import SubActivityPrint from '../../sub-activity/sub-activity-print';
import {
  canShowOptionLetter,
  parseMultipleChoiceTypes,
  sortOptions,
} from '../multiple-choice.utils';
import { isSingleSelect } from '../logic/is-single-select';
import { getOptionStatus } from '../logic/get-option-status';
import { MultipleChoiceLetter } from '../multiple-choice';
import * as Styled from './multiple-choice-print-list.style';
import type { MultipleChoiceAnswerFragment } from '../../../present/activity-answers.graphql.generated';
import MultipleChoicePrintAnswersRadio from './multiple-choice-print-answers-radio';
import MultipleChoicePrintAnswersCheckbox from './multiple-choice-print-answers-checkbox';
import type { Pluck } from 'lib/gql/pluck';

const MultipleChoicePrintAnswersList = ({
  properties,
  subActivityProperties,
  answers,
  showAnswer,
}: PrintProps & {
  answers: Pluck<MultipleChoiceAnswerFragment, 'multipleChoiceItems'>[];
  showAnswer: boolean;
}) => {
  const { multipleChoice } = properties;

  const sortedOptions = useMemo(
    () => sortOptions(multipleChoice.options),
    [multipleChoice.options]
  );

  const { isFree } = parseMultipleChoiceTypes(properties);

  const hasAnswered = useMemo(() => {
    return answers.length > 0;
  }, [answers]);

  const isOneOptionCorrect = useMemo(
    () =>
      isSingleSelect({
        answerMode: multipleChoice.answerMode,
        isFreeAnswerMultiSelect: multipleChoice.isFreeAnswerMultiSelect,
        options: multipleChoice.options,
      }),
    [multipleChoice]
  );

  return (
    <SubActivityPrint subActivityDetails={subActivityProperties}>
      <Styled.MultipleChoicePrintList>
        {sortedOptions.map((option, index) => {
          const isOptionSelected = !!answers.find(
            a => a.itemId === option.itemId && a.isSelected
          );

          const { isCorrect, isIncorrect, isSelected } = getOptionStatus({
            hasAnswered,
            showAnswer: !isFree && showAnswer,
            isOptionSelected,
            option,
          });

          const content = (
            <>
              {canShowOptionLetter(
                properties.multipleChoice.showOptionLetter
              ) && <MultipleChoiceLetter index={index} />}
              <Styled.MultipleChoicePrintListText>
                {option.text}
              </Styled.MultipleChoicePrintListText>
            </>
          );
          return isOneOptionCorrect ? (
            <MultipleChoicePrintAnswersRadio
              label={content}
              key={index}
              isCorrect={isCorrect}
              isIncorrect={isIncorrect}
              isSelected={isSelected}
            />
          ) : (
            <MultipleChoicePrintAnswersCheckbox
              key={index}
              label={content}
              disabled={hasAnswered && !isOptionSelected}
              isCorrect={isCorrect}
              isIncorrect={isIncorrect}
              isSelected={isSelected}
            />
          );
        })}
      </Styled.MultipleChoicePrintList>
    </SubActivityPrint>
  );
};

export default MultipleChoicePrintAnswersList;
